import { Injectable, inject } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Store, select } from '@ngrx/store'
import { from } from 'rxjs'
import { map, switchMap, tap, withLatestFrom } from 'rxjs/operators'
import { MeasuresService } from 'src/services/data/measures.service'
import { ToasterService } from 'src/shared/toaster/toaster.service'
import { catchAndLog } from 'src/shared/utils/catch-and-log'
import { createMeasure, createMeasureFailure, createMeasureSuccess, deleteMeasure, deleteMeasureFailure, deleteMeasureSuccess, loadMeasures, loadMeasuresFailure, loadMeasuresSuccess, updateMeasure, updateMeasureFailure, updateMeasureSuccess } from './measure.actions'
import { selectMeasureEntities } from './measure.selectors'

@Injectable()
export class MeasureEffects {
  private actions$ = inject(Actions)
  private Measures = inject(MeasuresService)
  private toaster = inject(ToasterService)
  private store = inject(Store)


  loadMeasures$ = createEffect(() => this.actions$.pipe(
    ofType(loadMeasures),
    switchMap(() =>
      from(this.Measures.getMeasures()).pipe(
        map(measures => loadMeasuresSuccess({ measures })),
        catchAndLog(this.toaster, 'Unable to fetch measures', loadMeasuresFailure
      )))))

  createMeasure$ = createEffect(() => this.actions$.pipe(
    ofType(createMeasure),
    withLatestFrom(this.store.pipe(select(selectMeasureEntities))),
    tap(() => this.toaster.progress('Creating measure...')),
    switchMap(([action, measures]) =>
      from(this.Measures.createMeasure(action.measure, measures)).pipe(
        map(measures => createMeasureSuccess({ measures })),
        tap(() => this.toaster.success('Measure created successfully.')),
        catchAndLog(this.toaster, 'Unable to create measure', createMeasureFailure
      )))))

  updateMeasure$ = createEffect(() => this.actions$.pipe(
    ofType(updateMeasure),
    withLatestFrom(this.store.pipe(select(selectMeasureEntities))),
    tap(() => this.toaster.progress('Updating measure...')),
    switchMap(([action, measures]) =>
      from(this.Measures.updateMeasure({ ...action.measure, measure_id: action.id }, measures)).pipe(
        map(measures => updateMeasureSuccess({ measures })),
        tap(() => this.toaster.success('Measure updated successfully.')),
        catchAndLog(this.toaster, 'Unable to update measure', updateMeasureFailure
      )))))

  deleteMeasure$ = createEffect(() => this.actions$.pipe(
    ofType(deleteMeasure),
    tap(() => this.toaster.progress('Deleting measure...')),
    switchMap(action =>
      from(this.Measures.remove(action.id)).pipe(
        map(r => deleteMeasureSuccess({ id: action.id })),
        tap(() => this.toaster.success('Measure deleted successfully.')),
        catchAndLog(this.toaster, 'Unable to delete measure', deleteMeasureFailure
      )))))
}
